import React, { useEffect, useRef } from "react";
import { CoordinateSystem } from "./CoordinateSystem";
import * as Maths from "oc-commons/Maths";
import * as Maths3 from "oc-threejs/Maths3";
import * as Maths2 from "oc-threejs/Maths2";
import {
  changeStreckenLaenge,
  GrundrissKante,
  normalToKante,
  Strecke2,
} from "../../arrangement/grundrissFunctions";
import { NumberInput } from "../../commons/NumberInput";
import { formatM } from "../../commons/Utils";
import classnames from "classnames";
import * as _ from "lodash";
import { Segment } from "../../arrangement/Segment";
import { laengeDisplayPxMinimum, punkteZuLinie } from "./Zaunlinien";
import { getSegmentEndenAusgleichForEcke } from "../../arrangement/elementFunctions";

export function Zaunlinie({
  cs,
  strecke,
  mode,
  kanteId,
  editedKanteId,
  setEditedKanteId,
  offset,
  editable,
  style,
  forSnapshot = false,
}: {
  cs: CoordinateSystem;
  strecke: Strecke2;
  mode: "SVG-zindex-0" | "SVG-zindex-1" | "HTML";
  kanteId?: number;
  editedKanteId?: number;
  setEditedKanteId?: (index: number) => void;
  offset?: number;
  editable?: boolean;
  style: "Tor" | "GanzesSegment" | "Segment" | "GrundrissKante" | "Schiebetorüberstand";
  forSnapshot?: boolean;
}) {
  const editMode = editable && editedKanteId == kanteId;
  const setEditMode = (mode: boolean) => {
    if (editable) {
      setEditedKanteId(mode ? kanteId : null);
    }
  };
  const inputRef = useRef(null);

  const grundriss = APP.arrangement.grundriss;
  const rulerOffsetPx = offset ?? 50;
  const rulerQuerPx = 6;
  const lineWidth = cs.lengthToScreen(0.15);
  const punkte = strecke.map((p) => cs.pointToScreen(Maths3.v2To3xz(p)));
  const normal = cs
    .sizeToScreen(Maths3.v2To3xz(normalToKante(strecke, grundriss.middlePoint)))
    .normalize();
  const querversatz = (p, offsetPx) => p.clone().addScaledVector(normal, offsetPx);
  const direction = Maths2.getDirection(punkte[0], punkte[1]);

  let segmentEndenAusgleichAnEcke = [0, 0];
  let segmentEndenAusgleich = 0;
  let laenge = 0;
  if (style == "GrundrissKante") {
    const grundriss = APP.arrangement.grundriss;
    const kante = grundriss.kanten.find((k) => k.id === kanteId);
    ({ laenge, segmentEndenAusgleich, segmentEndenAusgleichAnEcke } = getKanteInfo(kante));
  } else {
    laenge = Maths2.distance(strecke);
  }

  const rulerPunkte = punkte.map((p, i) =>
    querversatz(p, rulerOffsetPx).addScaledVector(
      direction,
      (i ? 1 : -1) * cs.lengthToScreen(segmentEndenAusgleichAnEcke[i])
    )
  );
  const rulerQuer = rulerPunkte.map((p, i) =>
    [-rulerQuerPx, +rulerQuerPx].map((q) =>
      querversatz(p, q).addScaledVector(direction, (i ? -1 : 1) * rulerQuerPx)
    )
  );

  const rulerMiddle = Maths2.middle(rulerPunkte);

  const setLaenge = (laengeNeu: number) => {
    const distanceNeu = Math.max(0, Maths.round(laengeNeu + segmentEndenAusgleich, 4));
    const grundriss = APP.arrangement.grundriss;
    changeStreckenLaenge(grundriss.kanten, grundriss.kantenIdToChangeCount, kanteId, distanceNeu);
    APP.logUserAction({ action: "setStreckenLaenge", laengeNeu });
    APP.dirty();
  };
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!editMode) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setEditMode(false);
        (document.activeElement as HTMLElement)?.blur();
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [editMode]);

  const selectedClassName = editMode ? "selected" : "";

  const zoomZuKleinFürDetails = cs.lengthToScreen(laenge) < laengeDisplayPxMinimum;

  // Berechne Skalierungsfaktor für mass-labels basierend auf Kamera-Distanz (nur für Snapshot)
  const labelScaleFactor = forSnapshot
    ? (() => {
        const cameraHeight = APP.camera.position.y;
        const normalCameraHeight = 32; // default Kamera-Höhe
        const factor = Math.max(1, cameraHeight / normalCameraHeight);
        return factor;
      })()
    : 1;

  const punkteZuPolygon = (punkte) =>
    punkte.map((p) => p.x.toFixed(1) + "," + p.y.toFixed(1)).join(" ");

  if (mode == "SVG-zindex-0") {
    if (zoomZuKleinFürDetails) {
      return null;
    }
    return (
      <>
        <line
          {...punkteZuLinie(rulerPunkte)}
          className={"svg-line-ruler " + selectedClassName}
        ></line>
        {[0, 1].map((eckIndex) => (
          <polygon
            key={eckIndex}
            points={punkteZuPolygon([rulerPunkte[eckIndex], ...rulerQuer[eckIndex]])}
            className={"svg-line-ruler-arrow " + selectedClassName}
          />
        ))}
      </>
    );
  }
  if (mode == "SVG-zindex-1") {
    return (
      <>
        {punkte.map((p, index) => (
          <circle key={index} cx={p.x.toFixed(1)} cy={p.y.toFixed(1)} r={5} className="svg-ecke" />
        ))}
        <line
          {...punkteZuLinie(punkte)}
          strokeWidth={lineWidth.toFixed(1)}
          className="svg-zaunline"
        ></line>
      </>
    );
  }
  if (zoomZuKleinFürDetails) {
    return null;
  }
  return (
    <>
      <div
        ref={containerRef}
        style={{ left: rulerMiddle.x + "px", top: rulerMiddle.y + "px" }}
        className={classnames(
          "ruler-length-input ",
          selectedClassName,
          editMode ? "z-index-2" : "z-index-0",
          editable && "editable"
        )}
        onClick={(e) => {
          if (editable && e.button === 0) {
            setEditMode(true);
          }
        }}
      >
        <span className="mass-label" style={{ fontSize: labelScaleFactor + "em" }}>
          {" "}
          {formatM(laenge, 2)}{" "}
        </span>
        {editable && (
          <NumberInput
            innerRef={(el) => {
              inputRef.current = el;
            }}
            onFocus={() => editable && setEditMode(true)}
            onBlur={() => setEditMode(false)}
            className="abstandInput"
            value={laenge}
            setValue={setLaenge}
            decimals={2}
            unit={"m"}
            min={0}
            max={1000}
            disabled={false}
            type={"number"}
            roundToMinMax={true}
            ariaLabel={I18N("Eingabefeld für Zaunlänge")}
          />
        )}
      </div>
    </>
  );
}

export function getKanteInfo(kante: GrundrissKante) {
  const segmentEndenAusgleichAnEcke = kante.ecken.map((e) =>
    getSegmentEndenAusgleichForEcke(e.segmentEcke)
  );
  const segmentEndenAusgleich = _.sum(segmentEndenAusgleichAnEcke);
  const distance = Maths2.distance(kante.ecken.map((e) => e.punkt) as Strecke2);
  const laenge = roundLaenge(distance - segmentEndenAusgleich);
  return { laenge, segmentEndenAusgleich, segmentEndenAusgleichAnEcke };
}

export function roundLaenge(v: number) {
  return Maths.round(v, 4);
}
